// The EDIT drawers — the sites recipe: drawers off the peek, one door per
// FACET (the judged story's edit half; ADR-038 drawer kind). TWO doors because
// the model has two write authorities: the RECORD drawer is the `edit` verb's
// fact patch (product · identity · condition + the supplier/warranty pair —
// `asset:write`, no event, audit-only); ACQUISITION is the books facet
// (`asset.books:write`, the full money record incl. the same dual-homed pair —
// the server gates per FIELD, so the two forms can never disagree about
// authority). Location and status are absent by design — moves are event
// verbs, reached through the register's actions.
//
// Both submit only what CHANGED since the drawer opened (the edit verb audits
// diffs — an unchanged echo would be audit noise). The edit rides the row stamp
// (ADR-084): each drawer seeds from the read it opens on (the record from the
// detail read, acquisition from the books read — both carry the unit's stamp),
// sends the stamp with the patch, and a stale save shows the line with Load the
// latest — that same read again. The IP address is deliberately NOT here: the
// detail read doesn't carry the address (L7 — the access facet is its own
// read), and the Access tab is the declared editor (2026-08-07 ruling).

import { useMemo, useRef } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';

import type { AssetBooks, AssetDetail } from '@ems-app/schemas/assets/responses';
import { ASSET_CATEGORY_DESCRIPTORS, ratingUnitFor, type AssetCategory } from '@ems-app/schemas/assets/categories';
import type { EditAssetInput } from '@ems-app/schemas/assets/verbs';
import type { ObservationFamily } from '@ems-app/schemas/observations/registry';
import { apiClient } from '../../lib/apiClient.js';
import { assetSource, assetsKeys, fetchAssetBooks, fetchAssetDetail } from '../../hooks/useAssets.js';
import { useCan } from '../../hooks/useCan.js';
import { useDisplayZone } from '../../hooks/useDisplayZone.js';
import { todayIn } from '../../lib/period.js';
import { useProject } from '../../hooks/useProject.js';
import { useFormShell } from '../../components/patterns/forms/useFormShell.js';
import { DrawerForm } from '../../components/patterns/forms/DrawerForm.js';
import { readLatest } from '../../components/patterns/forms/readLatest.js';
import { CATEGORY_ICON, InviteStrip, draftObservationFamilies } from '../../components/patterns/assetsDisplay.js';
import { Picker } from '../../components/patterns/Picker.js';
import { useAssetFace } from '../../components/patterns/lookupFaces.js';
import { useToast } from '../../components/ui/Toast.js';
import {
  AssetFormSchema,
  BooksFields,
  ConditionNetworkFields,
  EMPTY_ASSET_FORM,
  IdentityFields,
  ProductFields,
  SupplierWarrantyFields,
  buildLocalModelBody,
  suggestReference,
  productRowOf,
  useAssetFormCtx,
  useProductLookup,
  type AssetFormValues,
} from '../../components/patterns/assetFormShared.js';

const trimOrNull = (s: string): string | null => (s.trim() === '' ? null : s.trim());

// ── the RECORD drawer (the `edit` verb's fact patch — asset:write) ───────────

export function AssetEditDrawer({
  projectId,
  asset,
  open,
  onClose,
}: {
  projectId: string;
  asset: AssetDetail;
  open: boolean;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation('assets');
  const toast = useToast();
  const queryClient = useQueryClient();
  const can = useCan(projectId);
  // A local product born by an earlier attempt of this save (its patch refused — a stale unit, a refused fact): the
  // retry attaches it again instead of creating a twin; a product typed differently is a new one.
  const bornModel = useRef<{ body: string; id: string } | null>(null);
  // A grade's found-on day is the PROJECT's today (ADR-082 §6), never the browser's.
  const display = useDisplayZone({ kind: 'project', projectId });
  const today = todayIn(display.zone ?? display.viewerZone ?? 'UTC');

  const shell = useFormShell<AssetFormValues, AssetDetail>({
    schema: AssetFormSchema,
    row: asset,
    values: (r) => {
      const a = r ?? asset;
      return {
        ...EMPTY_ASSET_FORM,
        category: a.category,
        modelKey: a.model?.id ?? null,
        ratingValue: a.rating_value,
        ratingUnit: a.rating_unit,
        reference: a.reference,
        referenceAlt: a.reference_alt ?? '',
        serial: a.serial_number ?? '',
        name: a.name ?? '',
        unitCount: a.unit_count,
        condition: a.condition,
        soh: a.state_of_health_pct,
        supplier: a.supplier ?? '',
        warrantyEndOn: a.warranty_end_on,
        parentId: a.parent_asset_id,
      };
    },
    // The fresh unit: a quiet read (a unit gone meanwhile is said in the form), into the page's entry once it answers.
    reload: () => readLatest(queryClient, assetsKeys.detail(projectId, asset.id), () => fetchAssetDetail(projectId, asset.id, true)),
    onSubmit: async (v, { stamp, changed }) => {
      // local birth chains model-create → the fact patch (the picker's last row)
      let modelId: string | null | undefined;
      if (v.localMode && v.localModelName.trim()) {
        const body = buildLocalModelBody(v);
        const key = JSON.stringify(body);
        if (bornModel.current?.body !== key) {
          const created = await apiClient.post<{ id: string }>(`/projects/${projectId}/asset-models`, body);
          bornModel.current = { body: key, id: created.data.id };
        }
        modelId = bornModel.current.id;
      } else if ('modelKey' in changed) {
        modelId = v.modelKey;
      }
      const generic = v.modelKey !== null && models.known.get(v.modelKey)?.is_generic === true;
      const facts: NonNullable<EditAssetInput['facts']> = {
        ...(modelId !== undefined ? { assetModelId: modelId } : {}),
        ...('reference' in changed ? { reference: v.reference.trim() } : {}),
        ...('referenceAlt' in changed ? { referenceAlt: trimOrNull(v.referenceAlt) } : {}),
        ...('serial' in changed ? { serialNumber: trimOrNull(v.serial) } : {}),
        ...('name' in changed ? { name: trimOrNull(v.name) } : {}),
        ...('unitCount' in changed ? { unitCount: v.unitCount } : {}),
        // The unit is DERIVED, NEVER ASKED (the charter law) — it always
        // travels WITH the value so a generic row whose stored unit is null
        // (or stale) can still take a rating through this drawer.
        ...('ratingValue' in changed || modelId !== undefined
          ? {
              ratingValue: generic ? v.ratingValue : null,
              ratingUnit:
                generic && v.ratingValue !== null
                  ? (ratingUnitFor(asset.category as AssetCategory) as NonNullable<EditAssetInput['facts']>['ratingUnit'])
                  : null,
            }
          : {}),
        // the grade's found-on day stamps with the correction (pair law)
        ...('condition' in changed ? { condition: v.condition, conditionOn: v.condition ? today : null } : {}),
        ...('soh' in changed ? { stateOfHealthPct: v.soh, stateOfHealthOn: v.soh !== null ? today : null } : {}),
        ...('parentId' in changed ? { parentAssetId: v.parentId } : {}),
      };
      const acquisition: EditAssetInput['acquisition'] =
        'supplier' in changed || 'warrantyEndOn' in changed
          ? {
              ...('supplier' in changed ? { supplier: trimOrNull(v.supplier) } : {}),
              ...('warrantyEndOn' in changed ? { warrantyEndOn: v.warrantyEndOn } : {}),
            }
          : undefined;
      if (Object.keys(facts).length === 0 && !acquisition) return;
      await apiClient.patch(`/projects/${projectId}/assets/${asset.id}`, {
        row_version: stamp,
        ...(Object.keys(facts).length > 0 ? { facts } : {}),
        ...(acquisition ? { acquisition } : {}),
      });
      await queryClient.invalidateQueries({ queryKey: assetsKeys.all(projectId) });
      toast.success(t('edit.saved'));
    },
    onSuccess: onClose,
  });
  const { form } = shell;

  const category = asset.category as AssetCategory;
  // The saved product names itself before any read (the search names no ids); the answer's row replaces it.
  const knownModel = useMemo(() => (asset.model ? [productRowOf(asset.model, category)] : []), [asset.model, category]);
  const models = useProductLookup(projectId, category, { known: knownModel });
  const ctx = useAssetFormCtx({ t, projectId, form, models, canModels: can('asset.models:manage'), narrow: true });

  const v = form.watch();
  const invited = useMemo(() => {
    const fams = draftObservationFamilies({
      category,
      register: {
        modelSelected: (!!v.modelKey && !ctx.generic) || (v.localMode && v.localModelName.trim() !== ''),
        generic: ctx.generic && !v.localMode,
        ratingValue: v.ratingValue,
        unitCount: v.unitCount,
        hasTrackedMembers: asset.children.length > 0,
        serial: v.serial,
        // the address isn't editable here — the server's verdict passes through below
        reachDeclared: true,
        networkCapable: false,
        deployed: asset.status === 'deployed',
        parentId: v.parentId,
        condition: v.condition,
        soh: v.soh,
      },
      books: null,
    });
    return asset.observations.includes('asset.missing_reach') ? (['asset.missing_reach', ...fams] as ObservationFamily[]) : fams;
  }, [category, v, ctx.generic, asset.children.length, asset.status, asset.observations]);

  // The ASSOCIATE half of the composition rulings (the charter walk): an
  // EXISTING member joins / leaves / changes its group here — the wizard covers
  // birth, this drawer covers the rest. "Part of" is the unit lookup
  // inside THIS asset's site under `part_of` (what may hold it, in service,
  // never itself nor its own parts — the server applies the charter, ADR-078
  // §6); the saved parent is NAMED by the lookup's rows of given ids, nothing
  // is asked while the drawer is closed; the verb re-checks legality.
  const parentCats = ASSET_CATEGORY_DESCRIPTORS[category].parentCategories ?? [];
  const siteId = asset.location?.kind === 'site' ? asset.location.site_id : null;
  const assetFace = useAssetFace();
  const parentSource = useMemo(
    () => assetSource(projectId, 'part_of', assetFace, { site: siteId ?? '', placing: category, self: asset.id }),
    [projectId, assetFace, siteId, category, asset.id],
  );

  const Glyph = CATEGORY_ICON[category];
  return (
    <DrawerForm
      shell={shell}
      open={open}
      onClose={onClose}
      title={t('edit.asset')}
      description={t('edit.assetHint')}
      submitLabel={t('edit.submit')}
    >
      <div className="flex flex-col gap-6">
        {/* category locked — a different category is a different thing */}
        <div className="flex items-center gap-2">
          <Glyph size={20} aria-hidden className="text-[var(--color-text-secondary)]" />
          <span className="text-sm font-semibold text-[var(--color-text-primary)]">{t(`category.${category}`)}</span>
        </div>
        <ProductFields d={ctx} />
        <IdentityFields
          d={ctx}
          onGenerate={() => form.setValue('reference', suggestReference(category), { shouldDirty: true })}
        />
        <ConditionNetworkFields d={ctx} />
        {parentCats.length > 0 && siteId && (
          <Controller
            control={form.control}
            name="parentId"
            render={({ field, fieldState }) => (
              <Picker
                source={parentSource}
                // the site never changes here — the declaration the dependent source asks for (ADR-080 §9)
                dependsOn={{ value: siteId, case: 'must_belong' }}
                label={t('form.fields.parent')}
                placeholder={t('form.fields.parentNone')}
                value={field.value}
                onChange={field.onChange}
                clearable
                enabled={open}
                error={fieldState.error?.message}
              />
            )}
          />
        )}
        <SupplierWarrantyFields d={ctx} />
        <InviteStrip families={invited} lang={i18n.language} t={t} />
      </div>
    </DrawerForm>
  );
}

// ── the ACQUISITION drawer (the books facet — asset.books:write) ─────────────

export function AssetBooksDrawer({
  projectId,
  assetId,
  category,
  reference,
  books,
  open,
  onClose,
}: {
  projectId: string;
  assetId: string;
  category: AssetCategory;
  /** The row's real reference — the shared form schema requires one even
   *  though this facet never renders it (the one-body pattern's price). */
  reference: string;
  books: AssetBooks;
  open: boolean;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation('assets');
  const toast = useToast();
  const queryClient = useQueryClient();
  const can = useCan(projectId);
  const projectCurrency = useProject(projectId).data?.default_currency ?? '';

  const shell = useFormShell<AssetFormValues, AssetBooks>({
    schema: AssetFormSchema,
    row: books,
    values: (r) => {
      const b = r ?? books;
      return {
        ...EMPTY_ASSET_FORM,
        category,
        reference,
        booksOwner: b.owner?.id ?? null,
        booksMode: b.acquisition_mode ?? 'purchased',
        acquiredOn: b.acquired_on,
        acquiredCondition: b.acquired_condition,
        cost: b.acquisition_cost,
        currency: b.acquisition_currency ?? projectCurrency, // a row without books is born with the project's base currency as its default
        supplier: b.supplier ?? '',
        warrantyEndOn: b.warranty_end_on,
      };
    },
    // The fresh books: a quiet read (a unit gone meanwhile is said in the form), into the page's entry once it answers.
    reload: () => readLatest(queryClient, assetsKeys.books(projectId, assetId), () => fetchAssetBooks(projectId, assetId, true)),
    onSubmit: async (v, { stamp, changed }) => {
      const acquisition: NonNullable<EditAssetInput['acquisition']> = {
        ...('booksOwner' in changed ? { ownerId: v.booksOwner } : {}),
        ...('booksMode' in changed ? { acquisitionMode: v.booksMode } : {}),
        ...('acquiredOn' in changed ? { acquiredOn: v.acquiredOn } : {}),
        ...('acquiredCondition' in changed ? { acquiredCondition: v.acquiredCondition } : {}),
        ...('cost' in changed ? { acquisitionCost: v.cost } : {}),
        ...('cost' in changed || 'currency' in changed ? { acquisitionCurrency: v.cost !== null ? v.currency : null } : {}),
        ...('supplier' in changed ? { supplier: trimOrNull(v.supplier) } : {}),
        ...('warrantyEndOn' in changed ? { warrantyEndOn: v.warrantyEndOn } : {}),
      };
      if (Object.keys(acquisition).length === 0) return;
      await apiClient.patch(`/projects/${projectId}/assets/${assetId}`, { row_version: stamp, acquisition });
      await queryClient.invalidateQueries({ queryKey: assetsKeys.all(projectId) });
      toast.success(t('edit.saved'));
    },
    onSuccess: onClose,
  });
  const { form } = shell;

  // no product context in this drawer — the product lookup stays cold
  const models = useProductLookup(projectId, '');
  const ctx = useAssetFormCtx({ t, projectId, form, models, canModels: can('asset.models:manage'), narrow: true });

  const v = form.watch();
  const invited = useMemo(
    () =>
      draftObservationFamilies({
        category,
        register: null,
        books: { ownerId: v.booksOwner, cost: v.cost, acquiredOn: v.acquiredOn, mode: v.booksMode },
      }),
    [category, v.booksOwner, v.cost, v.acquiredOn, v.booksMode],
  );

  return (
    <DrawerForm
      shell={shell}
      open={open}
      onClose={onClose}
      title={t('edit.books')}
      description={t('edit.booksHint')}
      submitLabel={t('edit.submit')}
    >
      <div className="flex flex-col gap-6">
        <BooksFields d={ctx} />
        <InviteStrip families={invited} lang={i18n.language} t={t} />
      </div>
    </DrawerForm>
  );
}
